import { supabase } from "@/integrations/supabase/client";
import { primarySkuIdFromLineItems } from "@/lib/celebrationCheckout";
import type { ApprovedCelebrationLineItem } from "@/hooks/queries/useCelebrations";
import {
  vendorProductToSkuCardData,
  type VendorProductCheckoutRow,
} from "@/lib/vendorProductToSkuCardData";
import type { SkuCardData } from "@/components/chat/SkuCard";
import type { BulkRunRow } from "@/hooks/useBulkCelebrations";

export type BulkCheckoutSeed = {
  bulkRunId: string;
  bulkPeriodKey: string;
  celebrationIds: string[];
  recipientLabel: string;
  defaultDate: string;
  defaultAddress?: string;
  occasion: string;
};

export type OpenBulkCheckoutResult =
  | { kind: "checkout"; sku: SkuCardData; seed: BulkCheckoutSeed }
  | { kind: "error"; message: string };

export async function openBulkCelebrationCheckout(
  run: BulkRunRow,
  recipientLabel: string,
  celebrationIds: string[],
  defaultDate: string,
  defaultAddress?: string,
): Promise<OpenBulkCheckoutResult> {
  if (!run.recommendation_id) {
    return {
      kind: "error",
      message: "Approve the shared dessert recommendation before checkout.",
    };
  }

  const { data: rec, error: recErr } = await supabase
    .from("celebration_recommendations")
    .select("id, line_items, status")
    .eq("id", run.recommendation_id)
    .maybeSingle();

  if (recErr || !rec || rec.status !== "approved") {
    return { kind: "error", message: "Couldn't load the bulk recommendation." };
  }

  const skuId = primarySkuIdFromLineItems(
    rec.line_items as unknown as ApprovedCelebrationLineItem[],
  );
  if (!skuId) {
    return { kind: "error", message: "No dessert SKU on this bulk recommendation yet." };
  }

  const { data: product, error: prodErr } = await supabase
    .from("vendor_products")
    .select(
      "id, name, description, price, image_url, slug, vendor_category, lead_time_days, is_voucher, variant_label, dietary_tags, vendor_partners(vendor_name)",
    )
    .eq("id", skuId)
    .maybeSingle();

  if (prodErr || !product) {
    return { kind: "error", message: "Couldn't load the product for checkout." };
  }

  const sku = vendorProductToSkuCardData(product as VendorProductCheckoutRow);
  return {
    kind: "checkout",
    sku,
    seed: {
      bulkRunId: run.id,
      bulkPeriodKey: run.period_key,
      celebrationIds,
      recipientLabel,
      defaultDate,
      defaultAddress,
      occasion: "bulk_period",
    },
  };
}
